import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { locationService, Location } from '@/firebase/locationService';
import { floorService, Floor } from '@/firebase/floorService';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { formService, Form } from '@/firebase/formService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { getTasksForOrganization, TaskWithDetails } from '@/services/taskService';
import { assetHasPendingWork } from '@/services/floorMapAssetStatusService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { MapPin, ChevronLeft, AlertCircle, Maximize2, ClipboardList, LayoutPanelLeft, Filter } from 'lucide-react';
import ViewTaskModal from './ViewTaskModal';
import FloorManagementPanel from './FloorManagementPanel';

const OPEN_STATUSES = ['pending', 'overdue', 'pending_review'];

const FloorMapView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const locationIdFromUrl = searchParams.get('locationId');
  const floorIdFromUrl = searchParams.get('floorId');
  const { currentOrganization } = useOrganization();
  const { availableLocations } = useLocationFilter();

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(locationIdFromUrl || '');
  const [floors, setFloors] = useState<Floor[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<string>(floorIdFromUrl || '');
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [checklistCompletions, setChecklistCompletions] = useState<ChecklistCompletion[]>([]);
  const [formCompletions, setFormCompletions] = useState<FormCompletion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewingTask, setViewingTask] = useState<TaskWithDetails | null>(null);
  const [hoveredAsset, setHoveredAsset] = useState<Asset | null>(null);
  const [imageAspect, setImageAspect] = useState<number | null>(null);
  const [showAssignedTasks, setShowAssignedTasks] = useState(true);
  const [selectedComplianceTemplateIds, setSelectedComplianceTemplateIds] = useState<Set<string>>(new Set());
  const [showTaskTypeFilter, setShowTaskTypeFilter] = useState(false);
  const [showAssetTypeFilter, setShowAssetTypeFilter] = useState(false);
  const [selectedAssetTypeIds, setSelectedAssetTypeIds] = useState<Set<string>>(new Set());
  const [showManagePanel, setShowManagePanel] = useState(false);

  useEffect(() => {
    if (locationIdFromUrl) setSelectedLocationId(locationIdFromUrl);
    if (floorIdFromUrl) setSelectedFloorId(floorIdFromUrl);
  }, [locationIdFromUrl, floorIdFromUrl]);

  useEffect(() => {
    if (availableLocations?.length) {
      setLocations(availableLocations);
    } else if (currentOrganization?.id) {
      locationService.getAllLocations(currentOrganization.id).then((r) => {
        if (r.success && r.data) setLocations(r.data);
      });
    } else {
      setLocations([]);
    }
  }, [availableLocations, currentOrganization?.id]);

  const loadFloorsForLocation = React.useCallback(async () => {
    if (!selectedLocationId || !currentOrganization?.id) {
      setFloors([]);
      setSelectedFloorId('');
      return;
    }
    const r = await floorService.getFloorsByLocation(selectedLocationId, currentOrganization.id);
    if (r.success && r.data) {
      setFloors(r.data);
      setSelectedFloorId((prev) => {
        if (r.data!.length === 0) return '';
        if (!prev) return r.data![0].id || '';
        if (!r.data!.some((f) => f.id === prev)) return r.data![0]?.id || '';
        return prev;
      });
    } else {
      setFloors([]);
      setSelectedFloorId('');
    }
  }, [selectedLocationId, currentOrganization?.id]);

  useEffect(() => {
    loadFloorsForLocation();
  }, [loadFloorsForLocation]);

  useEffect(() => {
    setSelectedFloor(floors.find((f) => f.id === selectedFloorId) || null);
    setImageAspect(null); // Reset when floor changes - new image will set it
  }, [floors, selectedFloorId]);

  const loadTasks = React.useCallback(() => {
    if (!currentOrganization?.id || !selectedLocationId) {
      setTasks([]);
      return Promise.resolve();
    }
    setIsLoading(true);
    return getTasksForOrganization(currentOrganization.id).then((orgTasks) => {
      const locationTasks = orgTasks.filter(
        (t) => t.locationId === selectedLocationId && OPEN_STATUSES.includes(t.status)
      );
      setTasks(locationTasks);
      setIsLoading(false);
    });
  }, [currentOrganization?.id, selectedLocationId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!selectedFloorId || !currentOrganization?.id) {
      setAssets([]);
      return;
    }
    assetService.getAssetsByFloor(selectedFloorId, currentOrganization.id).then((r) => {
      setAssets(r.success && r.data ? r.data : []);
    });
  }, [selectedFloorId, currentOrganization?.id]);

  useEffect(() => {
    if (!currentOrganization?.id) {
      setAssetTypes([]);
      return;
    }
    assetService.getAllAssetTypes(currentOrganization.id).then((r) => {
      setAssetTypes(r.success && r.data ? r.data : []);
    });
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!currentOrganization?.id) {
      setChecklists([]);
      setForms([]);
      return;
    }
    Promise.all([
      checklistService.getAllChecklists(currentOrganization.id),
      formService.getAllForms(currentOrganization.id),
    ]).then(([clRes, fRes]) => {
      setChecklists(clRes.success && clRes.data ? clRes.data : []);
      setForms(fRes.success && fRes.data ? fRes.data : []);
    });
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!selectedLocationId) {
      setChecklistCompletions([]);
      setFormCompletions([]);
      return;
    }
    Promise.all([
      checklistCompletionService.getCompletionsByLocation(selectedLocationId),
      formCompletionService.getCompletionsByLocation(selectedLocationId),
    ]).then(([clRes, fRes]) => {
      setChecklistCompletions(clRes.success && clRes.data ? clRes.data : []);
      setFormCompletions(fRes.success && fRes.data ? fRes.data : []);
    });
  }, [selectedLocationId]);

  // Compliance tasks (templates) that apply to assets on this floor – for per-task filter
  const complianceTasksOnFloor = React.useMemo(() => {
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }
    const seen = new Map<string, string>();
    for (const asset of assets) {
      if (!asset.id || !asset.floorMapPosition) continue;
      const assetType = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) : undefined;
      const templateIds = assetType?.complianceTemplateIds ?? [];
      for (const task of tasks) {
        if (task.locationId === asset.locationId && templateIds.includes(task.templateId)) {
          if (!seen.has(task.templateId)) seen.set(task.templateId, task.templateName || task.templateId);
        }
      }
    }
    return Array.from(seen.entries()).map(([templateId, templateName]) => ({ templateId, templateName }));
  }, [tasks, assets, assetTypes]);

  // Initialize selected compliance templates when floor or compliance tasks change
  const prevComplianceRef = React.useRef<string>('');
  useEffect(() => {
    const key = complianceTasksOnFloor.map((t) => t.templateId).sort().join(',');
    if (key === prevComplianceRef.current) return;
    prevComplianceRef.current = key;
    setSelectedComplianceTemplateIds(new Set(complianceTasksOnFloor.map((t) => t.templateId)));
  }, [complianceTasksOnFloor]);

  const toggleComplianceTemplate = (templateId: string) => {
    setSelectedComplianceTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  };

  // Asset types that have assets on this floor (for filter)
  const assetTypesOnFloor = React.useMemo(() => {
    const typeIds = new Set<string>();
    assets.forEach((a) => {
      if (a.assetTypeId && a.floorMapPosition) typeIds.add(a.assetTypeId);
    });
    return assetTypes.filter((at) => at.id && typeIds.has(at.id));
  }, [assets, assetTypes]);

  // Initialize asset type filter to all when floor changes
  const prevFloorIdRef = React.useRef<string>(selectedFloorId);
  useEffect(() => {
    if (assetTypesOnFloor.length === 0) return;
    const ids = assetTypesOnFloor.map((at) => at.id!).filter(Boolean);
    const floorChanged = prevFloorIdRef.current !== selectedFloorId;
    if (floorChanged) prevFloorIdRef.current = selectedFloorId;
    setSelectedAssetTypeIds((prev) => {
      const hasStaleIds = [...prev].some((id) => !ids.includes(id));
      if (floorChanged || hasStaleIds || prev.size === 0) return new Set(ids);
      return prev;
    });
  }, [selectedFloorId, assetTypesOnFloor]);

  const toggleAssetType = (assetTypeId: string) => {
    setSelectedAssetTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetTypeId)) next.delete(assetTypeId);
      else next.add(assetTypeId);
      return next;
    });
  };

  // Build map: assetId -> tasks that reference it. Filter by task type: assigned vs compliance.
  const assetToTasks = React.useMemo(() => {
    const map = new Map<string, TaskWithDetails[]>();
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }

    const addTaskToAsset = (aid: string, task: TaskWithDetails) => {
      if (!map.has(aid)) map.set(aid, []);
      const list = map.get(aid)!;
      if (!list.includes(task)) list.push(task);
    };

    // (1) Assigned to assets: tasks with assetIds
    if (showAssignedTasks) {
      for (const task of tasks) {
        if (task.assetIds) {
          for (const aid of task.assetIds) {
            addTaskToAsset(aid, task);
          }
        }
      }
    }

    // (2) Compliance tasks: derived from assetType.complianceTemplateIds (only selected templates)
    if (selectedComplianceTemplateIds.size > 0) {
      for (const asset of assets) {
        if (!asset.id) continue;
        const assetType = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) : undefined;
        const templateIds = assetType?.complianceTemplateIds ?? [];
        for (const task of tasks) {
          if (
            task.locationId === asset.locationId &&
            templateIds.includes(task.templateId) &&
            selectedComplianceTemplateIds.has(task.templateId)
          ) {
            addTaskToAsset(asset.id, task);
          }
        }
      }
    }
    return map;
  }, [tasks, assets, assetTypes, showAssignedTasks, selectedComplianceTemplateIds]);

  // All tagged assets - red = has pending work (checklist/evidence) for this asset, green = all done or no tasks
  const assetPoints = React.useMemo(() => {
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }
    return assets
      .filter((a) => a.id && a.floorMapPosition)
      .map((asset) => {
        const applyingTasks = assetToTasks.get(asset.id!) ?? [];
        const templateIds = assetTypeMap.get(asset.assetTypeId)?.complianceTemplateIds ?? [];
        const hasPending = applyingTasks.length > 0 && assetHasPendingWork({
          asset,
          applyingTasks,
          assetTypeTemplateIds: templateIds,
          checklists,
          forms,
          checklistCompletions,
          formCompletions,
        });
        return {
          x: asset.floorMapPosition!.x,
          y: asset.floorMapPosition!.y,
          asset,
          hasPendingWork: hasPending,
          applyingTasks,
        };
      });
  }, [assets, assetToTasks, assetTypes, checklists, forms, checklistCompletions, formCompletions]);

  // Filtered points by asset type
  const filteredAssetPoints = React.useMemo(() => {
    if (selectedAssetTypeIds.size === 0) return assetPoints; // No filter or all unchecked = show all
    return assetPoints.filter((p) => p.asset.assetTypeId && selectedAssetTypeIds.has(p.asset.assetTypeId));
  }, [assetPoints, selectedAssetTypeIds]);

  const openFullView = () => {
    if (!selectedLocationId || !selectedFloorId) return;
    const url = `${window.location.origin}/floor-map/full?locationId=${selectedLocationId}&floorId=${selectedFloorId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const tolerance = 0.05;
    const point = filteredAssetPoints.find(
      (p) => Math.abs(p.x - x) < tolerance && Math.abs(p.y - y) < tolerance
    );
    if (point?.asset?.id) {
      window.open(`/asset/${point.asset.id}`, '_blank', 'noopener,noreferrer');
    } else {
      openFullView();
    }
  };

  const handlePointMouseEnter = (p: { asset: Asset; hasPendingWork: boolean }) => {
    setHoveredAsset(p.asset);
  };

  const handlePointMouseLeave = () => {
    setHoveredAsset(null);
  };

  const openTasksForFloor = React.useMemo(() => {
    const assetsWithPending = new Set(
      filteredAssetPoints.filter((p) => p.hasPendingWork).map((p) => p.asset.id).filter(Boolean)
    );
    const floorTasks = new Set<TaskWithDetails>();
    for (const [assetId, taskList] of assetToTasks) {
      if (assetsWithPending.has(assetId)) {
        for (const t of taskList) floorTasks.add(t);
      }
    }
    return Array.from(floorTasks);
  }, [assetToTasks, filteredAssetPoints]);

  const selectedLocation = locations.find((l) => l.id === selectedLocationId);

  return (
    <div className="p-4 max-w-[95vw] mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <a
            href="/locations"
            className="text-gray-600 hover:text-gray-900 flex items-center gap-1 text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Locations
          </a>
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary-600" />
            Floor Map
          </h2>
          <span className="text-sm text-gray-500 hidden sm:inline">
            View maps and manage floors &amp; floor plans for any location.
          </span>
        </div>

        <div className="flex flex-wrap gap-4 mb-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select location...</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Floor</label>
            <select
              value={selectedFloorId}
              onChange={(e) => setSelectedFloorId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              disabled={!selectedLocationId || floors.length === 0}
            >
              <option value="">Select floor...</option>
              {floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          {selectedFloorId && assetTypesOnFloor.length > 1 && (
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">Asset types</label>
              <button
                type="button"
                onClick={() => setShowAssetTypeFilter((s) => !s)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  showAssetTypeFilter
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Filter by asset type"
              >
                <Filter className="w-4 h-4" />
                <span>Asset types</span>
              </button>
              {showAssetTypeFilter && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowAssetTypeFilter(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute left-0 top-full mt-1 py-2 px-3 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[180px]">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Show asset types</p>
                    {assetTypesOnFloor.map((at) => (
                      <label
                        key={at.id}
                        className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAssetTypeIds.size === 0 || selectedAssetTypeIds.has(at.id!)}
                          onChange={() => toggleAssetType(at.id!)}
                          className="rounded border-gray-400 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{at.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {selectedFloorId && (
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">Task types</label>
              <button
                type="button"
                onClick={() => setShowTaskTypeFilter((s) => !s)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  showTaskTypeFilter
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Filter by task type"
              >
                <ClipboardList className="w-4 h-4" />
                <span>Task types</span>
              </button>
              {showTaskTypeFilter && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowTaskTypeFilter(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute left-0 top-full mt-1 py-2 px-3 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px] max-h-[320px] overflow-y-auto">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Show tasks from</p>
                    <label className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2">
                      <input
                        type="checkbox"
                        checked={showAssignedTasks}
                        onChange={() => setShowAssignedTasks((s) => !s)}
                        className="rounded border-gray-400 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Assigned to assets</span>
                    </label>
                    {complianceTasksOnFloor.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 mt-2 mb-1 font-medium">Compliance tasks</p>
                        {complianceTasksOnFloor.map(({ templateId, templateName }) => (
                          <label
                            key={templateId}
                            className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2 pl-4"
                          >
                            <input
                              type="checkbox"
                              checked={selectedComplianceTemplateIds.has(templateId)}
                              onChange={() => toggleComplianceTemplate(templateId)}
                              className="rounded border-gray-400 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 truncate" title={templateName}>
                              {templateName}
                            </span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {selectedLocationId && (
            <button
              type="button"
              onClick={() => setShowManagePanel((s) => !s)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                showManagePanel
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title={showManagePanel ? 'Hide manage panel' : 'Manage floors & floor plans'}
            >
              <LayoutPanelLeft className="w-4 h-4" />
              <span>Manage floors & floor plans</span>
            </button>
          )}
        </div>

        {!selectedLocationId ? (
          <div className="text-center py-12 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Select a location above to view and manage floor maps</p>
          </div>
        ) : !selectedLocation?.id ? (
          <div className="text-center py-12 text-gray-500">
            {locations.length === 0 ? (
              <p>Loading locations…</p>
            ) : (
              <p>This location is no longer available. Choose another from the list above.</p>
            )}
          </div>
        ) : (
          <div className="w-full space-y-4 min-w-0">
              {!selectedFloorId || floors.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-10 px-4 text-center text-gray-600">
                  <p className="font-medium text-gray-800">No floors yet</p>
                  <p className="text-sm mt-2">
                    Click{' '}
                    <button
                      type="button"
                      onClick={() => setShowManagePanel(true)}
                      className="font-semibold text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
                    >
                      Manage floors &amp; floor plans
                    </button>{' '}
                    above to add floors and upload images. Then pick a floor above to view the map.
                  </p>
                </div>
              ) : !selectedFloor?.floorMapImageUrl ? (
                <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/50 py-10 px-4 text-center text-gray-700">
                  <p className="font-medium">No floor plan for &ldquo;{selectedFloor?.name}&rdquo;</p>
                  <p className="text-sm mt-2">
                    Click{' '}
                    <button
                      type="button"
                      onClick={() => setShowManagePanel(true)}
                      className="font-semibold text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
                    >
                      Manage floors &amp; floor plans
                    </button>{' '}
                    to upload a floor plan image, then select the floor again if needed.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2">
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                      <button
                        onClick={openFullView}
                        className="absolute top-2 right-2 z-20 p-2 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors flex items-center gap-1.5 text-sm"
                        title="Open full screen in new tab"
                      >
                        <Maximize2 className="w-4 h-4" />
                        Full screen
                      </button>
                      <div
                        className="relative w-full cursor-pointer overflow-hidden rounded-lg"
                        style={{ aspectRatio: imageAspect ? `${imageAspect}` : '4/3' }}
                        onClick={handleMapClick}
                      >
                        <img
                          src={selectedFloor!.floorMapImageUrl}
                          alt={selectedFloor!.name}
                          className="absolute inset-0 w-full h-full object-contain"
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (img.naturalWidth && img.naturalHeight) {
                              setImageAspect(img.naturalWidth / img.naturalHeight);
                            }
                          }}
                        />
                        {filteredAssetPoints.map((p, i) => (
                          <div
                            key={`${p.asset.id}-${i}`}
                            className={`absolute w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white shadow cursor-pointer hover:scale-125 transition-transform z-10 ${
                              p.hasPendingWork ? 'bg-red-500' : 'bg-green-500'
                            }`}
                            style={{
                              left: `${p.x * 100}%`,
                              top: `${p.y * 100}%`,
                            }}
                            onMouseEnter={() => handlePointMouseEnter(p)}
                            onMouseLeave={handlePointMouseLeave}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (p.asset.id) {
                                window.open(`/asset/${p.asset.id}`, '_blank', 'noopener,noreferrer');
                              } else {
                                openFullView();
                              }
                            }}
                            title={
                              p.hasPendingWork
                                ? `${p.asset.assetName} — pending checklist/evidence`
                                : p.asset.assetName
                            }
                          />
                        ))}
                      </div>
                      {hoveredAsset && (
                        <div className="absolute bottom-2 left-2 right-2 md:right-14 bg-black/70 text-white text-sm px-3 py-2 rounded z-20">
                          <span>
                            {hoveredAsset.assetName}
                            {(() => {
                              const p = filteredAssetPoints.find((ap) => ap.asset.id === hoveredAsset.id);
                              return p?.hasPendingWork ? ' — click for asset details' : '';
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      Open tasks on this floor ({openTasksForFloor.length})
                    </h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Red = pending checklist/evidence. Green = all done. Click any dot to open asset details.
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {openTasksForFloor.length === 0 ? (
                        <p className="text-sm text-gray-500">No open tasks with tagged assets on this floor.</p>
                      ) : (
                        openTasksForFloor.map((task) => (
                          <button
                            key={task.id}
                            onClick={() => setViewingTask(task)}
                            className="w-full text-left px-3 py-2 rounded border border-gray-200 bg-white hover:border-primary-400 hover:bg-primary-50 text-sm"
                          >
                            <span className="font-medium text-gray-900">{task.templateName}</span>
                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                              task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              task.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {task.status}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}

        {showManagePanel && selectedLocation && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-[110]"
              onClick={() => setShowManagePanel(false)}
              aria-hidden="true"
            />
            <FloorManagementPanel
              variant="drawer"
              location={selectedLocation}
              onClose={() => setShowManagePanel(false)}
              onFloorsChanged={loadFloorsForLocation}
              onViewFloorMap={(floorId) => {
                setSelectedFloorId(floorId);
              }}
            />
          </>
        )}
      </div>

      {viewingTask && (
        <ViewTaskModal
          task={viewingTask}
          onClose={() => setViewingTask(null)}
          showActions
          onTaskUpdated={loadTasks}
        />
      )}
    </div>
  );
};

export default FloorMapView;
